import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetContactSubmissionQuery, useUpdateContactSubmissionMutation, useDeleteContactSubmissionMutation } from '../../store/slices/apiSlice';
import { 
  ArrowLeft,
  Mail, 
  User, 
  Phone, 
  Calendar,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  XCircle,
  Reply,
  FileText,
  Clock,
  Globe,
  Monitor,
  Trash2
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import toast from 'react-hot-toast';

const ContactSubmissionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'pending' | 'replied' | 'resolved'>('pending');
  const [notes, setNotes] = useState('');
  
  // Fetch contact submission details
  const { 
    data: submissionData, 
    isLoading, 
    error, 
    refetch 
  } = useGetContactSubmissionQuery(Number(id));

  // Update contact submission mutation
  const [updateContactSubmission, { isLoading: isUpdating }] = useUpdateContactSubmissionMutation();

  // Delete contact submission mutation
  const [deleteContactSubmission, { isLoading: isDeleting }] = useDeleteContactSubmissionMutation();

  const submission = submissionData?.data;

  // Update state when submission data changes
  React.useEffect(() => {
    if (submission?.status) {
      setSelectedStatus(submission.status as 'pending' | 'replied' | 'resolved');
    }
  }, [submission?.status]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning" className="flex items-center gap-1"><AlertCircle className="w-3 h-3" />Pending</Badge>;
      case 'replied':
        return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Replied</Badge>;
      case 'resolved':
        return <Badge variant="default" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Resolved</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);

  const handleSendReply = () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }
    
    // TODO: Implement reply API call
    toast.success('Reply sent successfully');
    setShowReplyModal(false);
    setReplyMessage('');
    refetch();
  };

  const handleUpdateStatus = async () => {
    if (!submission) return;

    try {
      await updateContactSubmission({
        id: submission.id,
        status: selectedStatus,
        notes: notes.trim() || undefined
      }).unwrap();
      
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      setShowNotesModal(false);
      setNotes('');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update status');
    }
  };

  const handleMarkAsRead = async () => {
    if (!submission) return;

    try {
      await updateContactSubmission({
        id: submission.id,
        status: submission.status,
        notes: submission.notes || undefined
      }).unwrap();
      
      toast.success('Marked as read');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to mark as read');
    }
  };

  const handleDeleteSubmission = async () => {
    if (!submission) return;

    try {
      await deleteContactSubmission(submission.id).unwrap();
      toast.success('Contact submission deleted successfully');
      navigate('/admin/contact-submissions');
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to delete contact submission');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/contact-submissions')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Submissions
          </Button>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/contact-submissions')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Submissions
          </Button>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Submission Not Found</h3>
          <p className="text-gray-600 mb-4">
            The contact submission you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/admin/contact-submissions')}>
            Back to Submissions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/contact-submissions')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Submissions
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contact Submission #{submission.id}</h1>
            <p className="text-gray-600">Submitted on {formatDate(submission.created_at)}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getStatusBadge(submission.status)}
          {!submission.is_read && (
            <Badge variant="destructive">Unread</Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{submission.name}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{submission.email}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{submission.phone}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-900">{submission.subject}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Message */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Message</h2>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                {submission.message}
              </p>
            </div>
          </Card>

          {/* Admin Reply */}
          {submission.admin_reply && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Reply className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Admin Reply</h2>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {submission.admin_reply}
                </p>
                {submission.replied_at && (
                  <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Replied on {formatDate(submission.replied_at)}</span>
                    {submission.replied_by && (
                      <span>by Admin #{submission.replied_by}</span>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <Button
                onClick={() => setShowReplyModal(true)}
                className="w-full flex items-center gap-2"
                disabled={submission.status === 'resolved'}
              >
                <Reply className="w-4 h-4" />
                {submission.admin_reply ? 'Reply Again' : 'Send Reply'}
              </Button>
              
              <Button
                variant="outline"
                onClick={handleMarkAsRead}
                className="w-full"
                disabled={submission.is_read || isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Mark as Read'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedStatus('resolved');
                  setShowStatusModal(true);
                }}
                className="w-full"
                disabled={submission.status === 'resolved' || isUpdating}
              >
                Mark as Resolved
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  if (submission) {
                    setSelectedStatus(submission.status as 'pending' | 'replied' | 'resolved');
                  }
                  setShowStatusModal(true);
                }}
                className="w-full"
                disabled={isUpdating}
              >
                Change Status
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowNotesModal(true)}
                className="w-full"
                disabled={isUpdating}
              >
                Add Notes
              </Button>

              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(true)}
                className="w-full text-red-600 hover:text-red-700 hover:border-red-300"
                disabled={isUpdating || isDeleting}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Submission
              </Button>
            </div>
          </Card>

          {/* Technical Details */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Globe className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{submission.ip_address}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Agent</label>
                <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                  <Monitor className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span className="text-xs text-gray-900 break-all">{submission.user_agent}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Submitted</label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(submission.created_at)}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(submission.updated_at)}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
            {submission.notes ? (
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{submission.notes}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No notes added yet</p>
            )}
          </Card>
        </div>
      </div>

      {/* Reply Modal */}
      <Modal
        isOpen={showReplyModal}
        onClose={() => setShowReplyModal(false)}
        title="Send Reply"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <p className="text-sm text-gray-900">{submission.name} ({submission.email})</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <p className="text-sm text-gray-900">Re: {submission.subject}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reply Message</label>
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Type your reply here..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowReplyModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendReply}
              disabled={!replyMessage.trim()}
            >
              <Reply className="w-4 h-4 mr-2" />
              Send Reply
            </Button>
          </div>
        </div>
      </Modal>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Status"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as 'pending' | 'replied' | 'resolved')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="pending">Pending</option>
              <option value="replied">Replied</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add any notes about this status change..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowStatusModal(false);
                setNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateStatus}
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Add Notes"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add notes about this contact submission..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowNotesModal(false);
                setNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleUpdateStatus();
                setShowNotesModal(false);
              }}
              disabled={isUpdating || !notes.trim()}
            >
              {isUpdating ? 'Adding...' : 'Add Notes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Contact Submission"
        size="md"
      >
        {submission && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Contact Submission Details:</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <p><span className="font-medium">Name:</span> {submission.name}</p>
                <p><span className="font-medium">Email:</span> {submission.email}</p>
                <p><span className="font-medium">Subject:</span> {submission.subject}</p>
                <p><span className="font-medium">Status:</span> {submission.status}</p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteSubmission}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Submission
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ContactSubmissionDetails;
